import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, Download } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
// import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { SortableTable, type SortableColumn } from '@/components/ui/sortable-table';
import { StatTile } from '@/components/ui/stat-tile';
import { useBusinesses } from '@/features/business/use-businesses';
import { usePosLanes } from '@/features/pos/pos-lanes.api';
import { saveAsFile } from '@/lib/download';
import { formatCurrency, formatNumber } from '@/lib/format';
import { formatStoreTime, storeToday } from '@/lib/store-time';
import { SettlementSheet } from './settlement-sheet';
import { useSettlements, type SettlementBatch } from './settlements.api';

/**
 * Settlements — replaces the legacy `/finance/terminal`.
 *
 * A settlement is a terminal closing its batch, so its figures are the
 * processor's, dated by the close. The page's job is to say whether they agree
 * with the POS, batch by batch, and to be plain about why they do not agree
 * with the dashboard: the dashboard dates money by the sale and counts tenders
 * that never touch a terminal.
 */

const ANY = 'ANY';

/** A week back from `day`, inclusive of both ends. */
function weekEnding(day: string) {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 6);
  return date.toISOString().slice(0, 10);
}

export function SettlementsPage() {
  const { t } = useTranslation();

  const today = storeToday();
  // A week, not today: most lanes close once a day, in the evening, so a
  // single-day default reads as an empty report for most of the day.
  const [start, setStart] = useState(() => weekEnding(today));
  const [end, setEnd] = useState(today);
  const [businessId, setBusinessId] = useState(ANY);
  const [laneId, setLaneId] = useState(ANY);

  const [selected, setSelected] = useState<SettlementBatch | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data: businesses } = useBusinesses();
  const lanes = usePosLanes(businessId === ANY ? null : businessId);

  const report = useSettlements({
    start,
    end,
    businessId: businessId === ANY ? null : businessId,
    laneId: laneId === ANY ? null : laneId,
  });

  const batches = report.data?.batches ?? [];
  const totals = report.data?.totals;

  const fields: FilterField[] = [
    {
      id: 'store',
      label: t('settlements.store'),
      value: businessId,
      onChange: (value) => {
        setBusinessId(value);
        // A lane belongs to one store.
        setLaneId(ANY);
      },
      options: [
        { value: ANY, label: t('settlements.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    {
      id: 'start',
      label: t('settlements.from'),
      type: 'date',
      value: start,
      resetValue: start,
      onChange: setStart,
    },
    {
      id: 'end',
      label: t('settlements.to'),
      type: 'date',
      value: end,
      resetValue: end,
      onChange: setEnd,
    },
  ];

  // Lanes are listed per store, so the picker only exists once one is chosen.
  if (businessId !== ANY) {
    fields.splice(1, 0, {
      id: 'lane',
      label: t('settlements.lane'),
      value: laneId,
      resetValue: ANY,
      onChange: setLaneId,
      options: [
        { value: ANY, label: t('settlements.allLanes') },
        ...(lanes.data?.lanes ?? []).map((lane) => ({
          value: lane.id,
          label: lane.tag || lane.id,
        })),
      ],
    });
  }

  function openBatch(batch: SettlementBatch) {
    setSelected(batch);
    setSheetOpen(true);
  }

  // ---------------------------------------------------------------- export

  function runExport() {
    if (!batches.length) {
      toast.info(t('settlements.nothingToExport'));
      return;
    }
    const escape = (value: unknown) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [
      [
        t('settlements.closedAt'),
        t('settlements.store'),
        t('settlements.lane'),
        t('settlements.terminalId'),
        t('settlements.batchNumber'),
        t('settlements.credit'),
        t('settlements.debit'),
        t('settlements.cash'),
        t('settlements.transactionsColumn'),
        t('settlements.salesColumn'),
        t('settlements.refunds'),
        t('settlements.net'),
        t('settlements.posRecorded'),
        t('settlements.differenceColumn'),
        t('settlements.status'),
      ].join(','),
      ...batches.map((batch) =>
        [
          formatStoreTime(batch.settledAt),
          batch.businessName,
          batch.laneTag,
          batch.terminalId,
          batch.batchNumber,
          batch.credit.saleAmount,
          batch.debit.saleAmount,
          batch.cash.saleAmount,
          batch.saleCount,
          batch.sales,
          batch.refunds,
          batch.net,
          batch.compared ? batch.posSales : '',
          batch.compared ? batch.salesDifference : '',
          batch.status,
        ]
          .map(escape)
          .join(',')
      ),
    ].join('\n');
    saveAsFile(csv, `settlements-${start}-to-${end}.csv`, 'text/csv;charset=utf-8');
    toast.success(t('settlements.exported', { rows: batches.length }));
  }

  // --------------------------------------------------------------- columns

  const columns: SortableColumn<SettlementBatch>[] = [
    {
      key: 'settledAt',
      header: t('settlements.closedAt'),
      sortValue: (batch) => batch.settledAt,
      render: (batch) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatStoreTime(batch.settledAt)}
        </span>
      ),
    },
    {
      key: 'store',
      header: t('settlements.store'),
      sortValue: (batch) => batch.businessName ?? '',
      render: (batch) => batch.businessName || '—',
    },
    {
      key: 'lane',
      header: t('settlements.lane'),
      sortValue: (batch) => batch.laneTag ?? '',
      render: (batch) => (
        <span className="font-medium">{batch.laneTag || '—'}</span>
      ),
    },
    {
      key: 'transactions',
      header: t('settlements.transactionsColumn'),
      align: 'right',
      sortValue: (batch) => batch.saleCount,
      render: (batch) => formatNumber(batch.saleCount),
    },
    {
      key: 'sales',
      header: t('settlements.salesColumn'),
      align: 'right',
      sortValue: (batch) => batch.sales,
      render: (batch) => formatCurrency(batch.sales),
    },
    {
      key: 'refunds',
      header: t('settlements.refunds'),
      align: 'right',
      sortValue: (batch) => batch.refunds,
      render: (batch) => (batch.refunds ? formatCurrency(batch.refunds) : '—'),
    },
    // {
    //   key: 'posSales',
    //   header: t('settlements.posRecorded'),
    //   align: 'right',
    //   sortValue: (batch) => (batch.compared ? batch.posSales : -1),
    //   render: (batch) =>
    //     batch.compared ? formatCurrency(batch.posSales) : '—',
    // },
    // {
    //   key: 'difference',
    //   header: t('settlements.differenceColumn'),
    //   align: 'right',
    //   sortValue: (batch) =>
    //     batch.compared
    //       ? Math.abs(batch.salesDifference) + Math.abs(batch.refundsDifference)
    //       : -1,
    //   render: (batch) => <DifferenceCell batch={batch} />,
    // },
  ];

  // ---------------------------------------------------------------- totals

  // Feeds the hidden Difference tile below.
  // const notCompared = totals ? totals.batches - totals.comparedBatches : 0;
  // const differing = totals?.batchesWithDifference ?? 0;
  // const differenceSub = totals?.batches
  //   ? [
  //       differing
  //         ? t('settlements.differenceSome', {
  //             differing: formatNumber(differing),
  //             compared: formatNumber(totals.comparedBatches),
  //           })
  //         : t('settlements.differenceNone'),
  //       notCompared
  //         ? t('settlements.notCompared', { batches: formatNumber(notCompared) })
  //         : null,
  //     ]
  //       .filter(Boolean)
  //       .join(' · ')
  //   : undefined;

  const tile = { loading: report.isPending, busy: report.isFetching };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settlements.title')}
        description={t('settlements.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={report.isFetching}
            onClick={runExport}
          >
            <Download className="size-3.5" />
            <span className="hidden sm:inline">{t('settlements.export')}</span>
          </Button>
        }
      />

      <FilterBar busy={report.isFetching} fields={fields} />

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatTile
          label={t('settlements.sales')}
          value={formatCurrency(totals?.sales ?? 0)}
          tone="primary"
          sub={t('settlements.salesSub', {
            transactions: formatNumber(totals?.saleCount ?? 0),
            batches: formatNumber(totals?.batches ?? 0),
          })}
          tooltip={t('settlements.salesHint')}
          {...tile}
        />
        <StatTile
          label={t('settlements.credit')}
          value={formatCurrency(totals?.credit.saleAmount ?? 0)}
          sub={t('settlements.transactions', {
            transactions: formatNumber(totals?.credit.saleCount ?? 0),
          })}
          tooltip={t('settlements.creditHint')}
          {...tile}
        />
        <StatTile
          label={t('settlements.debit')}
          value={formatCurrency(totals?.debit.saleAmount ?? 0)}
          sub={t('settlements.transactions', {
            transactions: formatNumber(totals?.debit.saleCount ?? 0),
          })}
          tooltip={t('settlements.debitHint')}
          {...tile}
        />
        <StatTile
          label={t('settlements.cash')}
          value={formatCurrency(totals?.cash.saleAmount ?? 0)}
          sub={t('settlements.transactions', {
            transactions: formatNumber(totals?.cash.saleCount ?? 0),
          })}
          tooltip={t('settlements.cashHint')}
          {...tile}
        />
        <StatTile
          label={t('settlements.refunds')}
          value={formatCurrency(totals?.refunds ?? 0)}
          sub={t('settlements.transactions', {
            transactions: formatNumber(totals?.refundCount ?? 0),
          })}
          tooltip={t('settlements.refundsHint')}
          {...tile}
        />
        {/* <StatTile
          label={t('settlements.difference')}
          value={formatCurrency(totals?.salesDifference ?? 0)}
          tone={differing ? 'warn' : 'good'}
          sub={differenceSub}
          tooltip={t('settlements.differenceHint')}
          {...tile}
        /> */}
      </div>

      {report.error && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>
            {report.error instanceof Error
              ? report.error.message
              : t('settlements.failed')}
          </span>
        </div>
      )}

      <SortableTable
        rows={batches}
        columns={columns}
        rowKey={(batch) => batch.id}
        loading={report.isPending}
        busy={report.isFetching}
        pageSize={25}
        initialSort={{ key: 'settledAt', direction: 'desc' }}
        emptyMessage={t('settlements.noRows')}
        onRowClick={openBatch}
      />

      <SettlementSheet
        batch={selected}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}

// Renders the hidden Difference column above.
// function DifferenceCell({ batch }: { batch: SettlementBatch }) {
//   const { t } = useTranslation();
//   if (!batch.compared) {
//     return (
//       <span className="text-muted-foreground text-xs">
//         {t('settlements.notComparedShort')}
//       </span>
//     );
//   }
//   if (!batch.salesDifference && !batch.refundsDifference) {
//     return <Badge variant="success">{t('settlements.matches')}</Badge>;
//   }
//   return (
//     <Badge variant="warning" className="tabular-nums">
//       {formatCurrency(batch.salesDifference || batch.refundsDifference)}
//     </Badge>
//   );
// }
